import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  getDocs,
  getDoc,
  orderBy,
  doc,
  updateDoc,
  setDoc,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { UserData } from '../types/user';
import { userService } from '../services/userService';
import { ActiveSubscription, SubscriptionVia } from '../types/subscription';

export interface UserWithActiveSubscription {
  id: string;
  userData: UserData;
  activeSubscription: ActiveSubscription | null;
}

export interface UseUserManagementReturn {
  users: UserWithActiveSubscription[];
  loading: boolean;
  error: Error | null;
  isFromCache: boolean;
  refreshData: () => Promise<void>;
}

export function useUserManagement(): UseUserManagementReturn {
  const [users, setUsers] = useState<UserWithActiveSubscription[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchUsers = useCallback(async () => {
    console.log('[USER_MANAGEMENT] Fetching fresh users data...');
    setLoading(true);
    setError(null);

    try {
      // Fetch users using userService
      console.log('[USER_MANAGEMENT] Fetching users from Firestore...');
      const usersData = await userService.getAllUsers();

      // Fetch active subscriptions
      // Try bulk query first, fall back to individual fetches if permission error
      console.log('[USER_MANAGEMENT] Fetching active subscriptions from Firestore...');
      const activeSubscriptionsMap = new Map<string, ActiveSubscription>();
      
      try {
        // Try bulk query (works if user is admin)
        const activeSubscriptionsQuery = query(collection(db, 'activeSubscriptions'));
        const activeSubscriptionsSnapshot = await getDocs(activeSubscriptionsQuery);

        activeSubscriptionsSnapshot.forEach((doc) => {
          const data = doc.data();

          // Safely convert Firestore timestamp to date
          let lastUpdatedDate: Date;

          try {
            lastUpdatedDate = data.lastUpdated?.toDate ? data.lastUpdated.toDate() : new Date(data.lastUpdated || Date.now());
          } catch (error) {
            console.warn('Error converting lastUpdated date:', error, data.lastUpdated);
            lastUpdatedDate = new Date(); // Fallback to current date
          }

          const subscription: ActiveSubscription = {
            userId: doc.id,
            subscriptionType: data.subscriptionType || 'default',
            subscriptionVia: data.subscriptionVia as SubscriptionVia || 'default',
            lastUpdated: lastUpdatedDate,
            subscriptionChangeMessage: data.subscriptionChangeMessage || '',
            subscriptionChangeRead: data.subscriptionChangeRead ?? true,
          };

          // Add stripeDetails if present
          if (data.stripeDetails) {
            subscription.stripeDetails = {
              stripeCustomerId: data.stripeDetails.stripeCustomerId || null,
              subscriptionId: data.stripeDetails.subscriptionId || null,
              subscriptionStatus: data.stripeDetails.subscriptionStatus || null,
              priceId: data.stripeDetails.priceId || null,
            };
          }

          activeSubscriptionsMap.set(doc.id, subscription);
        });
      } catch (bulkError: any) {
        // If bulk query fails (permission error), fetch individually for each user
        if (bulkError?.code === 'permission-denied' || bulkError?.message?.includes('permission')) {
          console.warn('[USER_MANAGEMENT] Bulk query failed, fetching subscriptions individually...');
          
          // Fetch subscriptions one by one (slower but works)
          for (const { id } of usersData) {
            try {
              const subDoc = await getDoc(doc(db, 'activeSubscriptions', id));
              if (subDoc.exists()) {
                const data = subDoc.data();
                
                let lastUpdatedDate: Date;
                try {
                  lastUpdatedDate = data.lastUpdated?.toDate ? data.lastUpdated.toDate() : new Date(data.lastUpdated || Date.now());
                } catch (error) {
                  lastUpdatedDate = new Date();
                }

                const subscription: ActiveSubscription = {
                  userId: id,
                  subscriptionType: data.subscriptionType || 'default',
                  subscriptionVia: data.subscriptionVia as SubscriptionVia || 'default',
                  lastUpdated: lastUpdatedDate,
                  subscriptionChangeMessage: data.subscriptionChangeMessage || '',
                  subscriptionChangeRead: data.subscriptionChangeRead ?? true,
                };

                // Add stripeDetails if present
                if (data.stripeDetails) {
                  subscription.stripeDetails = {
                    stripeCustomerId: data.stripeDetails.stripeCustomerId || null,
                    subscriptionId: data.stripeDetails.subscriptionId || null,
                    subscriptionStatus: data.stripeDetails.subscriptionStatus || null,
                    priceId: data.stripeDetails.priceId || null,
                  };
                }

                activeSubscriptionsMap.set(id, subscription);
              }
            } catch (individualError) {
              // Skip users where subscription fetch fails
              console.warn(`[USER_MANAGEMENT] Failed to fetch subscription for user ${id}:`, individualError);
            }
          }
        } else {
          // Re-throw if it's not a permission error
          throw bulkError;
        }
      }

      // Combine users with their active subscriptions and sort by name
      const usersWithActiveSubscriptions: UserWithActiveSubscription[] = usersData
        .map(({ id, userData }) => ({
          id,
          userData,
          activeSubscription: activeSubscriptionsMap.get(id) || null,
        }))
        .sort((a, b) => (a.userData.name || '').localeCompare(b.userData.name || ''));

      console.log(`[USER_MANAGEMENT] Successfully fetched ${usersWithActiveSubscriptions.length} users`);

      setUsers(usersWithActiveSubscriptions);
      setError(null);

    } catch (err) {
      console.error('[USER_MANAGEMENT] Error fetching users:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch users'));
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshData = useCallback(async () => {
    await fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    users,
    loading,
    error,
    isFromCache: false, // Always false since we don't cache
    refreshData,
  };
} 